/**
 * Renderer de la popin de test de campagne Plume.
 *
 * Ce fichier adapte les données métier `EMailCampaignTest` au composant UI :
 * adresse de réception du test, contact CRM de référence, profils fictifs,
 * résultats d'envoi et actions de fermeture / confirmation.
 *
 * La sélection de contact de campagne est temporairement désactivée côté UX :
 * le premier contact exploitable reste affiché comme référence, tandis que les
 * profils fictifs restent éditables.
 */
import { renderLucideIcon } from '../utils/lucide-icons.js'
import { renderPopin } from './popin-renderer.js'
import {
  bindServerSearchSelect,
  renderServerSearchSelect,
  renderServerSearchSelectedItem
} from './server-search-select-renderer.js'

/**
 * Extrait et normalise le contrat UI `email-campaign-test` depuis une réponse
 * de step ou un objet de données imbriqué.
 */
function normalizeEmailCampaignTestUi(result) {
  if (!result || typeof result !== 'object') return null

  var ui = result.ui || result.emailCampaignTestUi || null
  if (!ui || typeof ui !== 'object') return null
  if (String(ui.type || '').toLowerCase() !== 'email-campaign-test') return null

  var confirmLabel = String(ui.confirmLabel || 'Envoyer les tests')
  if (confirmLabel === "Confirmer l'adresse") confirmLabel = 'Envoyer les tests'

  return {
    type: 'email-campaign-test',
    title: String(ui.title || 'Test de campagne'),
    message: String(ui.message || 'Choisissez l’adresse qui recevra les emails de test. Le premier contact de la campagne sert de profil CRM de référence.').trim(),
    batchId: String(ui.batchId || result.batchId || '').trim(),
    testEmail: String(ui.testEmail || result.testEmail || '').trim(),
    emailLabel: String(ui.emailLabel || 'Adresse de test'),
    emailPlaceholder: String(ui.emailPlaceholder || 'test@example.com'),
    profileLabel: String(ui.profileLabel || 'Contact de référence'),
    profileHelp: String(ui.profileHelp || "Le premier contact de la campagne est utilisé comme profil CRM de référence. Vous pouvez ajouter des profils fictifs pour tester d'autres personnalisations."),
    effectiveRecipientLabel: String(ui.effectiveRecipientLabel || 'Adresse de réception effective'),
    realContactNotice: String(ui.realContactNotice || "Les contacts réels servent uniquement de profils de personnalisation."),
    manualProfileLabel: String(ui.manualProfileLabel || 'Profils fictifs'),
    manualProfileHelp: String(ui.manualProfileHelp || 'Ajoutez un profil fictif pour tester des valeurs absentes du répertoire.'),
    addManualProfileLabel: String(ui.addManualProfileLabel || 'Ajouter un profil fictif'),
    generateManualProfilesLabel: String(ui.generateManualProfilesLabel || 'Générer des profils'),
    manualProfileNameLabel: String(ui.manualProfileNameLabel || 'Nom du profil'),
    manualProfileNamePlaceholder: String(ui.manualProfileNamePlaceholder || 'Profil fictif'),
    generatedManualProfileNamePrefix: String(ui.generatedManualProfileNamePrefix || 'Profil généré'),
    confirmLabel: confirmLabel,
    cancelLabel: String(ui.cancelLabel || 'Annuler'),
    error: String(ui.error || result.error || '').trim(),
    confirmed: ui.confirmed === true || result.confirmed === true,
    defaultSource: String(ui.defaultSource || '').trim(),
    selectedProfileIds: normalizeStringArray(ui.selectedProfileIds || result.selectedProfileIds),
    profileSearchQuery: String(ui.profileSearchQuery || result.profileSearchQuery || '').trim(),
    profiles: normalizeProfiles(ui.profiles || result.profiles),
    manualProfileFields: normalizeManualProfileFields(ui.manualProfileFields || result.manualProfileFields),
    testResults: normalizeTestResults(ui.testResults || result.testResults),
    testResultLabel: String(ui.testResultLabel || 'Résultat du test'),
    testQueuedLabel: String(ui.testQueuedLabel || 'Pris en charge'),
    testFailedLabel: String(ui.testFailedLabel || 'Échec')
  }
}

/**
 * Compose la popin complète à partir du contrat UI renvoyé par le step
 * EMailCampaignTest. Le widget injecte l'état de recherche courant avant rendu.
 */
function renderEmailCampaignTestUi(ui) {
  var status = ui.confirmed
    ? '<div class="wc-email-campaign-test-status ok">' + renderLucideIcon('circleCheck') + '<span>' + escapeHtml(ui.message || '') + '</span></div>'
    : ''
  var error = ui.error
    ? '<div class="wc-email-campaign-test-status error">' + renderLucideIcon('triangleAlert') + '<span>' + escapeHtml(ui.error) + '</span></div>'
    : ''
  var effectiveRecipient = ui.testEmail
    ? '<div class="wc-email-campaign-test-status info">' + renderLucideIcon('mailCheck') + '<span>' + escapeHtml(ui.effectiveRecipientLabel || 'Adresse de réception effective') + ' : <strong>' + escapeHtml(ui.testEmail) + '</strong></span></div>'
    : ''
  var results = renderTestResults(ui)
  var profiles = renderProfiles(ui)
  var manualProfiles = renderManualProfiles(ui)
  var headerMessage = ui.confirmed ? '' : escapeHtml(ui.message || '')

  return renderPopin({
    title: ui.title || 'Test de campagne',
    className: 'wc-email-campaign-test-ui',
    dialogClassName: 'wc-email-campaign-test-dialog',
    closeAttributes: 'data-popin-close data-email-campaign-test-close',
    header: (
      '<div class="wc-ds-capability wc-ds-capability-plume">' +
        '<div class="wc-ds-capability-name">' + renderLucideIcon('feather') + '<span>@Plume</span></div>' +
        '<div class="wc-ds-capability-meta">' + escapeHtml(ui.title || 'Test de campagne') + '</div>' +
      '</div>'
    ),
    body: (
      '<form class="wc-email-campaign-test-form">' +
          '<div class="wc-email-campaign-test-head">' +
            '<div class="wc-email-campaign-test-title">' + escapeHtml(ui.title || 'Test de campagne') + '</div>' +
            (headerMessage ? '<div class="wc-email-campaign-test-message sectionhelp">' + headerMessage + '</div>' : '') +
          '</div>' +
          error +
          status +
          results +
          effectiveRecipient +
          '<label class="wc-email-campaign-test-field">' +
            '<span class="subtitle">' + escapeHtml(ui.emailLabel || 'Adresse de test') + '</span>' +
            '<input type="email" data-email-campaign-test-address value="' + escapeAttributeValue(ui.testEmail || '') + '" placeholder="' + escapeAttributeValue(ui.emailPlaceholder || 'test@example.com') + '"' + (ui.confirmed ? ' disabled' : '') + '>' +
          '</label>' +
          profiles +
          manualProfiles +
          '<div class="wc-email-campaign-test-actions">' +
            (ui.confirmed
              ? '<button type="button" class="wc-email-campaign-test-secondary" data-email-campaign-test-close>' + renderLucideIcon('x') + '<span>Fermer</span></button>'
              : '<button type="submit" class="wc-email-campaign-test-confirm">' + renderLucideIcon('mailCheck') + '<span>' + escapeHtml(ui.confirmLabel || 'Envoyer les tests') + '</span></button>') +
          '</div>' +
        '</form>'
    )
  })
}

/**
 * Rend le récapitulatif des emails de test pris en charge ou en échec.
 */
function renderTestResults(ui) {
  var results = Array.isArray(ui.testResults) ? ui.testResults : []
  if (!results.length) return ''

  return (
    '<section class="wc-email-campaign-test-results">' +
      '<div class="wc-email-campaign-test-results-title">' + renderLucideIcon('listChecks') + '<span>' + escapeHtml(ui.testResultLabel || 'Résultat du test') + '</span></div>' +
      '<div class="wc-email-campaign-test-results-list">' +
        results.map(function (result) {
          var ok = result.status === 'queued'
          return (
            '<div class="wc-email-campaign-test-result ' + (ok ? 'ok' : 'error') + '">' +
              (ok ? renderLucideIcon('circleCheck') : renderLucideIcon('triangleAlert')) +
              '<span class="wc-email-campaign-test-result-body">' +
                '<span class="wc-email-campaign-test-result-line">' +
                  '<span class="wc-email-campaign-test-result-label">' + escapeHtml(result.profileLabel || result.profileId || '') + '</span>' +
                  '<span class="wc-email-campaign-test-result-status">' + escapeHtml(ok ? ui.testQueuedLabel || 'Pris en charge' : ui.testFailedLabel || 'Échec') + '</span>' +
                '</span>' +
                (result.error ? '<span class="wc-email-campaign-test-result-error">' + escapeHtml(result.error) + '</span>' : '') +
              '</span>' +
            '</div>'
          )
        }).join('') +
      '</div>' +
    '</section>'
  )
}

/**
 * Branche les actions principales de la popin : fermeture, confirmation,
 * recherche de profils et édition de profils fictifs.
 */
function bindEmailCampaignTestUi(el, ui, handlers) {
  if (!el || !ui) return
  var form = el.querySelector('.wc-email-campaign-test-form')
  var input = el.querySelector('[data-email-campaign-test-address]')
  if (!form || !input) return

  Array.prototype.slice.call(el.querySelectorAll('[data-email-campaign-test-close]')).forEach(function (button) {
    button.addEventListener('click', function () {
      if (handlers && typeof handlers.onClose === 'function') {
        handlers.onClose()
      }
    })
  })

  form.addEventListener('submit', function (event) {
    event.preventDefault()
    if (ui.confirmed) return
    if (el.querySelector('[data-email-campaign-test-manual-editor-form]')) return
    if (handlers && typeof handlers.onConfirm === 'function') {
      handlers.onConfirm({
        batchId: ui.batchId || null,
        testEmail: String(input.value || '').trim(),
        profileIds: Array.prototype.slice.call(el.querySelectorAll('[data-email-campaign-test-profile]:checked')).map(function (checkbox) {
          return String(checkbox.value || '').trim()
        }).filter(Boolean),
        manualProfiles: collectManualProfiles(el),
        label: ui.confirmLabel || 'Envoyer les tests'
      })
    }
  })

  var addManualProfileButton = el.querySelector('[data-email-campaign-test-add-manual]')
  var generateManualProfilesButton = el.querySelector('[data-email-campaign-test-generate-manual]')
  var manualEditor = el.querySelector('[data-email-campaign-test-manual-editor]')
  var selectedList = el.querySelector('[data-server-search-selected-list]')
  if (addManualProfileButton && manualEditor && selectedList) {
    addManualProfileButton.addEventListener('click', function () {
      if (ui.confirmed) return
      if (manualEditor.querySelector('[data-email-campaign-test-manual-editor-form]')) return
      var index = selectedList.querySelectorAll('[data-email-campaign-test-manual-profile]').length + 1
      var id = 'manual-' + index
      manualEditor.innerHTML = renderManualProfileEditor(ui, {
        id: id,
        label: '',
        origin: 'manual',
        personalization: {},
        selected: true
      }, index)
      setManualEditing(el, true)
      bindManualProfileEditor(el, ui)
    })
  }
  if (generateManualProfilesButton && selectedList) {
    generateManualProfilesButton.addEventListener('click', function () {
      if (ui.confirmed) return
      var startIndex = selectedList.querySelectorAll('[data-email-campaign-test-manual-profile]').length
      removeEmptySelectedState(selectedList)
      buildGeneratedManualProfiles(ui, startIndex).forEach(function (profile, offset) {
        selectedList.insertAdjacentHTML('beforeend', renderServerSearchSelectedItem({ disabled: false }, manualProfileToServerSearchItem(ui, profile, startIndex + offset + 1)))
      })
      bindProfilePicker(el, ui, handlers, input)
    })
  }

  bindProfilePicker(el, ui, handlers, input)
}

/**
 * Adapte les profils Plume au composant générique de recherche serveur. Les
 * sélectionnés restent séparés des résultats pour éviter de recharger une
 * grande campagne complète dans le contrôle.
 */
function renderProfiles(ui) {
  var profiles = Array.isArray(ui.profiles) ? ui.profiles : []
  var contactProfiles = profiles.filter(function (profile) { return profile.kind !== 'manual' })
  var manualProfiles = profiles.filter(function (profile) { return profile.kind === 'manual' && profile.selected !== false })
  var hasManualFields = Array.isArray(ui.manualProfileFields) && ui.manualProfileFields.length
  if (!contactProfiles.length && !manualProfiles.length && !hasManualFields) return ''
  var referenceContact = resolveReferenceContact(contactProfiles)
  var selectedContacts = referenceContact ? [referenceContact] : []

  return (
    '<fieldset class="wc-email-campaign-test-profiles">' +
      renderServerSearchSelect({
        name: 'email-campaign-test-profiles',
        label: ui.profileLabel || 'Profils de test',
        help: ui.profileHelp || '',
        labelClassName: 'wc-server-search-select-label subtitle',
        helpClassName: 'wc-server-search-select-help sectionhelp',
        noticeHtml: renderLucideIcon('info') + '<span>' + escapeHtml(ui.realContactNotice || '') + '</span>',
        icon: renderLucideIcon('users'),
        query: '',
        disabled: ui.confirmed,
        hideSearch: true,
        searchLabel: 'Ajouter un profil',
        searchLabelClassName: 'subtitle',
        searchPlaceholder: 'Rechercher un contact',
        emptySelectedLabel: 'Aucun contact de référence disponible.',
        emptyResultsLabel: 'Aucun contact trouvé.',
        idleLabel: '',
        loadingLabel: 'Recherche des profils...',
        selectedItems: selectedContacts.map(function (profile) { return profileToServerSearchItem(ui, profile, { locked: true }) }).concat(manualProfiles.map(function (profile, index) {
          return manualProfileToServerSearchItem(ui, profile, index + 1)
        })),
        resultItems: []
      }) +
    '</fieldset>'
  )
}

function resolveReferenceContact(profiles) {
  if (!profiles.length) return null
  return profiles.find(function (profile) { return profile.selected }) || profiles[0]
}

/**
 * Convertit un profil CRM Plume vers le format générique attendu par Server
 * Search Select, en conservant les inputs nécessaires au submit.
 */
function profileToServerSearchItem(ui, profile, options) {
  options = options || {}
  var details = []
  if (profile.email) details.push(profile.email)
  if (profile.sourceLabel) details.push(profile.sourceLabel)
  return {
    id: profile.id,
    label: profile.label || profile.id,
    meta: details.join('  '),
    warning: profile.warning || '',
    blockedReason: profile.blockedReason || '',
    disabled: !profile.selectable,
    locked: options.locked === true,
    hiddenHtml: '<input type="checkbox" data-email-campaign-test-profile value="' + escapeAttributeValue(profile.id) + '" checked hidden' + (ui.confirmed ? ' disabled' : '') + '>'
  }
}

/**
 * Convertit un profil fictif vers un item sélectionné du composant générique,
 * avec les valeurs personnalisées conservées dans des champs cachés.
 */
function manualProfileToServerSearchItem(ui, profile, index) {
  var fields = Array.isArray(ui.manualProfileFields) ? ui.manualProfileFields : []
  var id = String(profile.id || ('manual-' + index)).trim()
  var values = profile.personalization || profile.values || {}
  var label = String(profile.label || (ui.manualProfileNamePlaceholder || 'Profil fictif')).trim()
  var details = fields.map(function (field) {
    var value = values && Object.prototype.hasOwnProperty.call(values, field.key) ? values[field.key] : ''
    if (!value) return ''
    return (field.label || field.key) + ' : ' + value
  }).filter(Boolean)

  return {
    id: id,
    label: label,
    meta: details.join('  '),
    hiddenHtml: (
      '<input type="checkbox" data-email-campaign-test-profile value="' + escapeAttributeValue(id) + '" checked hidden' + (ui.confirmed ? ' disabled' : '') + '>' +
      '<span hidden data-email-campaign-test-manual-profile data-manual-profile-id="' + escapeAttributeValue(id) + '" data-manual-profile-origin="' + escapeAttributeValue(profile.origin || 'manual') + '">' +
        '<input type="hidden" data-email-campaign-test-manual-label value="' + escapeAttributeValue(label) + '">' +
        fields.map(function (field) {
          var value = values && Object.prototype.hasOwnProperty.call(values, field.key) ? values[field.key] : ''
          return '<input type="hidden" data-email-campaign-test-manual-value data-field-key="' + escapeAttributeValue(field.key) + '" value="' + escapeAttributeValue(value || '') + '">'
        }).join('') +
      '</span>'
    )
  }
}

/**
 * Branche le composant générique sur les actions métier Plume : recherche
 * serveur, ajout local d'un résultat et retrait d'une sélection.
 */
function bindProfilePicker(el, ui, handlers, emailInput) {
  bindServerSearchSelect(el, { name: 'email-campaign-test-profiles' }, {
    onRemove: function (payload) {
      if (payload.itemEl && payload.itemEl.parentNode) payload.itemEl.parentNode.removeChild(payload.itemEl)
    }
  })
}

/**
 * Collecte les identifiants des profils actuellement sélectionnés dans la
 * popin.
 */
function collectSelectedProfileIds(el) {
  return Array.prototype.slice.call(el.querySelectorAll('[data-email-campaign-test-profile]:checked')).map(function (checkbox) {
    return String(checkbox.value || '').trim()
  }).filter(Boolean)
}

/**
 * Retire l'état vide de la liste des profils sélectionnés lorsqu'un premier
 * élément est ajouté.
 */
function removeEmptySelectedState(selectedList) {
  if (!selectedList || !selectedList.querySelector) return
  var empty = selectedList.querySelector('[data-server-search-empty-selected], .wc-email-campaign-test-empty-selected')
  if (empty && empty.parentNode) empty.parentNode.removeChild(empty)
}

/**
 * Branche les boutons du formulaire de profil fictif : enregistrer ou annuler.
 */
function bindManualProfileEditor(el, ui) {
  var editor = el.querySelector('[data-email-campaign-test-manual-editor-form]')
  var selectedList = el.querySelector('[data-server-search-selected-list]')
  if (!editor || !selectedList) return
  var saveButton = editor.querySelector('[data-email-campaign-test-save-manual]')
  var cancelButton = editor.querySelector('[data-email-campaign-test-cancel-manual]')
  if (saveButton) {
    saveButton.addEventListener('click', function () {
      var profile = readManualProfileEditor(editor)
      if (!profile.label && !Object.keys(profile.personalization || {}).some(function (key) { return profile.personalization[key] })) return
      removeEmptySelectedState(selectedList)
      selectedList.insertAdjacentHTML('beforeend', renderServerSearchSelectedItem({ disabled: false }, manualProfileToServerSearchItem(ui, profile, selectedList.querySelectorAll('[data-email-campaign-test-manual-profile]').length + 1)))
      bindProfilePicker(el, ui, {}, null)
      clearManualProfileEditor(el)
    })
  }
  if (cancelButton) {
    cancelButton.addEventListener('click', function () {
      clearManualProfileEditor(el)
    })
  }
}

/**
 * Lit les valeurs saisies dans le formulaire de profil fictif.
 */
function readManualProfileEditor(editor) {
  var id = String(editor.getAttribute('data-manual-profile-id') || '').trim()
  var labelInput = editor.querySelector('[data-email-campaign-test-manual-label]')
  var values = {}
  Array.prototype.slice.call(editor.querySelectorAll('[data-email-campaign-test-manual-value]')).forEach(function (input) {
    var key = String(input.getAttribute('data-field-key') || '').trim()
    if (!key) return
    values[key] = String(input.value || '').trim()
  })
  return {
    kind: 'manual',
    id: id || null,
    label: labelInput ? String(labelInput.value || '').trim() : '',
    origin: String(editor.getAttribute('data-manual-profile-origin') || 'manual').trim() === 'generated' ? 'generated' : 'manual',
    selected: true,
    personalization: values
  }
}

/**
 * Vide le formulaire de profil fictif et quitte le mode édition.
 */
function clearManualProfileEditor(el) {
  var manualEditor = el.querySelector('[data-email-campaign-test-manual-editor]')
  if (manualEditor) manualEditor.innerHTML = ''
  setManualEditing(el, false)
}

/**
 * Active ou désactive l'état visuel indiquant qu'un profil fictif est en cours
 * d'édition.
 */
function setManualEditing(el, active) {
  var root = el.querySelector('.wc-email-campaign-test-ui') || el
  if (!root || !root.classList) return
  root.classList.toggle('is-editing-manual', active)
}

/**
 * Rend le bloc permettant d'ajouter ou de générer des profils fictifs.
 */
function renderManualProfiles(ui) {
  var fields = Array.isArray(ui.manualProfileFields) ? ui.manualProfileFields : []
  if (!fields.length) return ''

  return (
    '<section class="wc-email-campaign-test-manual">' +
      '<div class="wc-email-campaign-test-manual-head">' +
        '<div>' +
          '<div class="wc-email-campaign-test-manual-title subtitle">' + renderLucideIcon('userPlus') + '<span>' + escapeHtml(ui.manualProfileLabel || 'Profils fictifs') + '</span></div>' +
          '<div class="wc-email-campaign-test-profile-help sectionhelp">' + escapeHtml(ui.manualProfileHelp || '') + '</div>' +
        '</div>' +
        '<div class="wc-email-campaign-test-manual-buttons">' +
          '<button type="button" class="wc-email-campaign-test-add-manual" data-email-campaign-test-add-manual' + (ui.confirmed ? ' disabled' : '') + '>' + renderLucideIcon('plus') + '<span>' + escapeHtml(ui.addManualProfileLabel || 'Ajouter un profil fictif') + '</span></button>' +
          '<button type="button" class="wc-email-campaign-test-add-manual" data-email-campaign-test-generate-manual' + (ui.confirmed ? ' disabled' : '') + '>' + renderLucideIcon('wandSparkles') + '<span>' + escapeHtml(ui.generateManualProfilesLabel || 'Générer des profils') + '</span></button>' +
        '</div>' +
      '</div>' +
      '<div class="wc-email-campaign-test-manual-editor" data-email-campaign-test-manual-editor></div>' +
    '</section>'
  )
}

/**
 * Rend le formulaire de saisie d'un profil fictif.
 */
function renderManualProfileEditor(ui, profile, index) {
  var fields = Array.isArray(ui.manualProfileFields) ? ui.manualProfileFields : []
  var id = String(profile.id || ('manual-' + index)).trim()
  var values = profile.personalization || {}

  return (
    '<div class="wc-email-campaign-test-manual-profile" data-email-campaign-test-manual-editor-form data-manual-profile-id="' + escapeAttributeValue(id) + '" data-manual-profile-origin="' + escapeAttributeValue(profile.origin || 'manual') + '">' +
      '<label class="wc-email-campaign-test-field">' +
        '<span>' + escapeHtml(ui.manualProfileNameLabel || 'Nom du profil') + '</span>' +
        '<input type="text" data-email-campaign-test-manual-label value="' + escapeAttributeValue(profile.label || '') + '" placeholder="' + escapeAttributeValue(ui.manualProfileNamePlaceholder || 'Profil fictif') + '"' + (ui.confirmed ? ' disabled' : '') + '>' +
      '</label>' +
      '<div class="wc-email-campaign-test-manual-fields">' +
        fields.map(function (field) {
          var value = values && Object.prototype.hasOwnProperty.call(values, field.key) ? values[field.key] : ''
          return (
            '<label class="wc-email-campaign-test-field">' +
              '<span>' + escapeHtml(field.label || field.key) + '</span>' +
              '<input type="text" data-email-campaign-test-manual-value data-field-key="' + escapeAttributeValue(field.key) + '" value="' + escapeAttributeValue(value || '') + '" placeholder="' + escapeAttributeValue(field.valueExample || '') + '"' + (ui.confirmed ? ' disabled' : '') + '>' +
            '</label>'
          )
        }).join('') +
      '</div>' +
      '<div class="wc-email-campaign-test-manual-actions">' +
        '<button type="button" class="wc-email-campaign-test-secondary" data-email-campaign-test-cancel-manual>' + renderLucideIcon('x') + '<span>' + escapeHtml(ui.cancelLabel || 'Annuler') + '</span></button>' +
        '<button type="button" class="wc-email-campaign-test-save-manual" data-email-campaign-test-save-manual>' + renderLucideIcon('check') + '<span>Enregistrer</span></button>' +
      '</div>' +
    '</div>'
  )
}

/**
 * Collecte tous les profils fictifs enregistrés dans la popin pour les renvoyer
 * au step.
 */
function collectManualProfiles(el) {
  return Array.prototype.slice.call(el.querySelectorAll('[data-email-campaign-test-manual-profile]')).map(function (profileEl) {
    var id = String(profileEl.getAttribute('data-manual-profile-id') || '').trim()
    var labelInput = profileEl.querySelector('[data-email-campaign-test-manual-label]')
    var values = {}
    Array.prototype.slice.call(profileEl.querySelectorAll('[data-email-campaign-test-manual-value]')).forEach(function (input) {
      var key = String(input.getAttribute('data-field-key') || '').trim()
      if (!key) return
      values[key] = String(input.value || '').trim()
    })
    return {
      id: id || null,
      label: labelInput ? String(labelInput.value || '').trim() : '',
      origin: String(profileEl.getAttribute('data-manual-profile-origin') || 'manual').trim() === 'generated' ? 'generated' : 'manual',
      values: values
    }
  }).filter(function (profile) {
    return profile.label || Object.keys(profile.values || {}).some(function (key) { return profile.values[key] })
  })
}

/**
 * Normalise la liste de profils reçue du backend.
 */
function normalizeProfiles(value) {
  if (!Array.isArray(value)) return []

  return value.map(function (profile) {
    if (!profile || typeof profile !== 'object') return null
    var id = String(profile.id || profile.contactId || '').trim()
    if (!id) return null
    var kind = String(profile.kind || '').trim()
    if (!kind) kind = profile.contactId || profile.email ? 'contact' : 'manual'
    return {
      kind: kind,
      origin: String(profile.origin || '').trim() === 'generated' ? 'generated' : 'manual',
      id: id,
      contactId: String(profile.contactId || id).trim(),
      label: String(profile.label || id).trim(),
      email: String(profile.email || '').trim(),
      sourceLabel: String(profile.sourceLabel || '').trim(),
      selectable: profile.selectable !== false,
      selected: profile.selected === true,
      warning: String(profile.warning || '').trim(),
      blockedReason: String(profile.blockedReason || '').trim(),
      personalization: normalizeRecord(profile.personalization)
    }
  }).filter(Boolean)
}

/**
 * Génère quelques profils fictifs locaux à partir des champs de personnalisation
 * connus.
 */
function buildGeneratedManualProfiles(ui, startIndex) {
  var fields = Array.isArray(ui.manualProfileFields) ? ui.manualProfileFields : []
  var count = 3
  var labels = [
    ui.generatedManualProfileNamePrefix + ' 1',
    ui.generatedManualProfileNamePrefix + ' 2',
    ui.generatedManualProfileNamePrefix + ' 3'
  ]

  return Array.from({ length: count }).map(function (_, variantIndex) {
    var index = startIndex + variantIndex + 1
    var personalization = {}
    fields.forEach(function (field) {
      personalization[field.key] = generateManualProfileValue(field, variantIndex)
    })
    return {
      kind: 'manual',
      origin: 'generated',
      id: 'generated-' + index,
      label: labels[variantIndex] || (ui.generatedManualProfileNamePrefix + ' ' + String(index)),
      selected: true,
      selectable: true,
      personalization: personalization
    }
  })
}

/**
 * Produit une valeur de démonstration pour un champ de profil fictif.
 */
function generateManualProfileValue(field, variantIndex) {
  var key = String(field && field.key || '').toLowerCase()
  var example = String(field && field.valueExample || '').trim()
  var variants = {
    firstname: ['Camille', 'Nora', 'Alexis'],
    firstName: ['Camille', 'Nora', 'Alexis'],
    companyname: ['Nova Industrie', 'Atelier Boréal', 'Maison Lumen'],
    company: ['Nova Industrie', 'Atelier Boréal', 'Maison Lumen'],
    role: ['Directrice achats', 'Responsable opérations', 'DAF'],
    jobtitle: ['Directrice achats', 'Responsable opérations', 'DAF'],
    sector: ['Industrie', 'Services B2B', 'Distribution'],
    lastinteraction: ['rendez-vous mardi dernier', 'appel de découverte récent', 'relance restée sans réponse'],
    customerstatus: ['prospect chaud', 'client actif', 'opportunité à relancer'],
    email: ['test.persona1@example.test', 'test.persona2@example.test', 'test.persona3@example.test']
  }
  var values = variants[key] || null
  if (values) return values[variantIndex % values.length]
  if (example) return variantIndex === 0 ? example : example + ' ' + String(variantIndex + 1)
  return 'Valeur test ' + String(variantIndex + 1)
}

/**
 * Normalise les champs de personnalisation disponibles pour les profils
 * fictifs.
 */
function normalizeManualProfileFields(value) {
  if (!Array.isArray(value)) return []
  return value.map(function (field) {
    if (!field || typeof field !== 'object') return null
    var key = String(field.key || '').trim()
    if (!key) return null
    return {
      key: key,
      label: String(field.label || key).trim(),
      valueExample: String(field.valueExample || '').trim()
    }
  }).filter(Boolean)
}

/**
 * Normalise les résultats d'envoi de test.
 */
function normalizeTestResults(value) {
  if (!Array.isArray(value)) return []
  return value.map(function (result) {
    if (!result || typeof result !== 'object') return null
    return {
      profileId: String(result.profileId || '').trim(),
      profileLabel: String(result.profileLabel || '').trim(),
      status: String(result.status || '').trim() === 'queued' ? 'queued' : 'failed',
      emailId: String(result.emailId || '').trim(),
      error: String(result.error || '').trim()
    }
  }).filter(Boolean)
}

/**
 * Convertit un objet arbitraire en dictionnaire de chaînes.
 */
function normalizeRecord(value) {
  if (!value || typeof value !== 'object') return {}
  return Object.keys(value).reduce(function (acc, key) {
    acc[key] = value[key] == null ? '' : String(value[key])
    return acc
  }, {})
}

/**
 * Normalise un tableau de valeurs en tableau de chaînes non vides.
 */
function normalizeStringArray(value) {
  if (!Array.isArray(value)) return []
  return value.map(function (entry) {
    return String(entry || '').trim()
  }).filter(Boolean)
}

/**
 * Échappe une valeur destinée à du contenu HTML texte.
 */
function escapeHtml(value) {
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

/**
 * Échappe une valeur destinée à un attribut HTML.
 */
function escapeAttributeValue(value) {
  return escapeHtml(value).replace(/`/g, '&#96;')
}

export { normalizeEmailCampaignTestUi, renderEmailCampaignTestUi, bindEmailCampaignTestUi }
